<template>
    <portal to="topbar-portal">
        <transition name="md-fade" appear>
            <div
                v-if="open"
                class="md-panel"
                :style="panelStyle"
                role="listbox"
                :aria-label="$t('post.page.comments.mentions.aria') || 'Suggestions'"
                @mousedown.prevent
            >
                <div v-if="loading" class="md-state">
                    <i class="fal fa-spinner-third fa-spin"></i>
                    {{ $t('post.page.comments.mentions.searching') || 'Recherche…' }}
                </div>
                <div v-else-if="!users.length" class="md-state">
                    <i class="fal fa-user-slash"></i>
                    {{ query
                        ? ($t('post.page.comments.mentions.no_match', { query }) || `Aucun opérateur pour « ${query} »`)
                        : ($t('post.page.comments.mentions.empty') || 'Tape pour rechercher un opérateur') }}
                </div>
                <ul v-else class="md-list">
                    <li
                        v-for="(u, i) in users"
                        :key="u.id"
                        class="md-item"
                        :class="{ 'is-active': i === selectedIndex }"
                        role="option"
                        :aria-selected="i === selectedIndex ? 'true' : 'false'"
                        @mouseenter="$emit('hover', i)"
                        @click.stop="$emit('select', u)"
                    >
                        <span class="md-avatar">
                            <img v-if="u.profile_picture" :src="u.profile_picture" :alt="u.name" />
                            <span v-else>{{ initials(u.name) }}</span>
                        </span>
                        <span class="md-body">
                            <span class="md-name">{{ u.name }}</span>
                            <span v-if="u.email" class="md-email">{{ u.email }}</span>
                        </span>
                    </li>
                </ul>
            </div>
        </transition>
    </portal>
</template>

<script>
export default {
    name: 'MentionsDropdown',
    props: {
        open: { type: Boolean, default: false },
        users: { type: Array, default: () => [] },
        selectedIndex: { type: Number, default: 0 },
        position: { type: Object, default: () => ({ left: 0, top: 0 }) },
        loading: { type: Boolean, default: false },
        query: { type: String, default: '' }
    },
    computed: {
        panelStyle() {
            const style = {
                position: 'fixed',
                left: `${this.position.left}px`,
                zIndex: 10000
            }
            if (this.position.bottom != null) {
                style.bottom = `${this.position.bottom}px`
            } else {
                style.top = `${this.position.top || 0}px`
            }
            return style
        }
    },
    methods: {
        initials(name) {
            if (!name) return '?'
            const parts = name.split(/\s+/).filter(Boolean)
            return ((parts[0] || '').charAt(0) + (parts[1] || '').charAt(0)).toUpperCase() || '?'
        }
    }
}
</script>

<style>
.md-panel {
    width: 280px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    box-shadow:
        0 12px 28px rgba(15, 23, 42, 0.14),
        0 4px 10px rgba(15, 23, 42, 0.08);
    overflow: hidden;
    font-family: var(--v2-font-sans);
}

.md-state {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    color: var(--v2-text-3);
    font-size: var(--v2-text-sm-size);
}

.md-list {
    list-style: none;
    margin: 0;
    padding: 4px;
    max-height: 240px;
    overflow-y: auto;
}

.md-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 8px;
    border-radius: var(--v2-radius-sm);
    cursor: pointer;
    transition: background var(--v2-transition-fast);
}

.md-item.is-active,
.md-item:hover {
    background: var(--v2-primary-soft);
}

.md-avatar {
    width: 28px;
    height: 28px;
    border-radius: var(--v2-radius-full);
    background: var(--v2-primary);
    color: var(--v2-primary-contrast);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    font-weight: var(--v2-font-weight-semibold);
    flex-shrink: 0;
    overflow: hidden;
}

.md-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.md-body {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.md-name {
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-1);
    font-weight: var(--v2-font-weight-medium);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.md-email {
    font-size: 0.7rem;
    color: var(--v2-text-4);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.md-fade-enter-active,
.md-fade-leave-active {
    transition: opacity var(--v2-transition-base),
        transform var(--v2-transition-base);
}

.md-fade-enter,
.md-fade-leave-to {
    opacity: 0;
    transform: translateY(-4px) scale(0.98);
}
</style>
